import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import icon from "../assets/whiteIcon.svg";
import { createGeminiService, Message } from "../services/geminiService";
import {
  Conversation,
  conversationService,
} from "../services/conversationService";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/layout/Header";
import { generateAthleteReport } from "../utils/athleteReportUtils";
import { useDayPostList } from "../hooks/useDayPostData";
import { userService } from "../services/userService";
import { useSubscription } from "../providers/SubscriptionProvider";
import ComingSoon from "../components/ComingSoon";
import { FEATURE_GATES } from "../config/featureAccess";
const ChatPrompt: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [chat, setChat] = useState<Conversation>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isNewConversation, setIsNewConversation] = useState<boolean | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [failedUserMessage, setFailedUserMessage] = useState<string | null>(
    null,
  );
  const {
    subscription: userSubscription,
    hasSubscription,
    isPremium,
    refreshSubscription,
  } = useSubscription();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [warningTitle, setWarningTitle] = useState<string>("");
  // Use ref to track if initial message has been sent
  const initialMessageSent = useRef(false);

  // Get day posts data for athlete report
  const { dayPosts, loading: dayPostsLoading } = useDayPostList();

  // generateAthleteReport returns null whenever there's no Peak Score for
  // today, which is the normal state for a new or lapsed account. Sending the
  // model nothing at all in that case also strips the "don't guess"
  // instruction, precisely when it has the least to go on and is most likely
  // to invent a training history. So the empty case gets its own explicit
  // statement rather than no grounding.
  const NO_ATHLETE_DATA_REPORT =
    "I have not logged any training, wellness, sleep, recovery, niggle or Peak Score data. There is no recorded history for me at all.";

  const getAthleteReportText = (): string => {
    try {
      const recent7Days = dayPosts.slice(-7);
      return (
        generateAthleteReport(
          recent7Days,
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        ) ?? NO_ATHLETE_DATA_REPORT
      );
    } catch (error) {
      console.warn("Could not generate athlete report for context:", error);
      return NO_ATHLETE_DATA_REPORT;
    }
  };

  // Helper function to create context message with athlete report
  const createAthleteReportContext = (): Message[] => {
    const athleteReport = getAthleteReportText();

    const contextMessage: Message = {
      id: "athlete-context-0",
      role: "user",
      content: `ATHLETE CONTEXT DATA FOR THIS CONVERSATION:

${athleteReport}

Use this athlete report as the source of truth for my training, wellness, sleep, recovery, niggles, and Peak Score. Answer my questions using this data. If something is not in the report, say so instead of guessing — never invent sessions, numbers or dates.`,
      timestamp: new Date(),
    };

    const contextAck: Message = {
      id: "athlete-context-ack-0",
      role: "model",
      content:
        "I have your athlete report and will treat it as the source of truth. If something isn't in it, I'll tell you rather than guess.",
      timestamp: new Date(),
    };

    return [contextMessage, contextAck];
  };

  // Create gemini service with user context - use useMemo to maintain the same instance
  const geminiService = useMemo(() => {
    return currentUser
      ? createGeminiService(currentUser.uid)
      : createGeminiService();
  }, [currentUser?.uid]);

  // Load chat and messages first
  useEffect(() => {
    const loadChatData = async () => {
      if (!currentUser || !chatId) {
        console.log("No current user or chat ID");
        setDataLoaded(true); // Mark as loaded even if no data to prevent infinite loading
        return;
      }
      try {
        console.log("Loading chat data for chatId:", chatId);

        // Load chat first
        const loadedChat = await conversationService.loadChat(
          currentUser.uid,
          chatId,
        );
        if (!loadedChat) {
          console.log("No chat found");
          setDataLoaded(true);
          return;
        }

        // Load messages
        const loadedMessages = await conversationService.loadMessages(
          currentUser.uid,
          chatId,
        );

        // Set both chat and messages
        setChat(loadedChat);
        setMessages(loadedMessages);
        // A conversation is "new" if:
        // 1. It has exactly 1 user message and no assistant response, OR
        // 2. The last message is from a user (needs AI response)
        const hasOnlyUserMessage =
          loadedMessages.length === 1 && loadedMessages[0].role === "user";
        const lastMessageIsUser =
          loadedMessages.length > 0 &&
          loadedMessages[loadedMessages.length - 1].role === "user";
        const needsResponse = hasOnlyUserMessage || lastMessageIsUser;
        setIsNewConversation(needsResponse);
        console.log(
          "NEEDS AI RESPONSE",
          needsResponse,
          "Messages:",
          loadedMessages.length,
          "Roles:",
          loadedMessages.map((m) => m.role),
          "Last role:",
          loadedMessages[loadedMessages.length - 1]?.role,
        );
        setDataLoaded(true);
      } catch (err) {
        console.error("Error loading chat data:", err);
        setDataLoaded(true);
      }
    };
    loadChatData();
  }, [chatId, currentUser]);

  // Reset the ref and clear chat when chatId changes (new conversation)
  useEffect(() => {
    initialMessageSent.current = false;
    setChatInitialized(false);
    // Clear the gemini chat session when switching conversations
    geminiService.clearChat();
  }, [chatId, geminiService]);

  // Gemini initialization with ref-based duplicate prevention
  useEffect(() => {
    const initializeGeminiChat = async () => {
      if (
        !dataLoaded ||
        dayPostsLoading ||
        !chat ||
        chatInitialized ||
        isNewConversation === null
      ) {
        return;
      }

      try {
        if (isNewConversation) {
          const hasOnlyUserMessage =
            messages.length === 1 && messages[0].role === "user";
          const lastMessageIsUser =
            messages.length > 0 &&
            messages[messages.length - 1].role === "user";

          // Prevent sending initial message multiple times for brand new conversations
          if (hasOnlyUserMessage && initialMessageSent.current) {
            return;
          }

          if (hasOnlyUserMessage) {
            // Case 1: Brand new conversation with only one user message
            console.log(
              "🔥 NEW CONVERSATION FLOW - Starting fresh Gemini chat for new conversation",
            );
            setIsLoading(true);
            setErrorMessage(null);

            // Same pattern as daily insights: empty history, athlete data in the prompt.
            await geminiService.startChat([]);

            const athleteReport = getAthleteReportText();
            const prompt = `Here is my recent training and wellness data. Use it as the source of truth. If something is not in this data, say so instead of guessing — never invent sessions, numbers or dates.

${athleteReport}

My question: ${chat.firstMessage ?? ""}`;

            const response = await geminiService.sendMessage(prompt);

            const assistantMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: "model",
              content: response,
              timestamp: new Date(),
            };

            setMessages((prev) => [...prev, assistantMessage]);
            await conversationService.saveMessage(
              currentUser!.uid,
              chat.id!,
              assistantMessage,
            );

            initialMessageSent.current = true;
            setIsLoading(false);
          } else if (lastMessageIsUser) {
            // Case 2: Existing conversation that ends with a user message needing response
            console.log(
              "🔄 CONTINUING CONVERSATION - Last message needs AI response",
            );
            setIsLoading(true);
            setErrorMessage(null);

            // Include athlete report context at the beginning
            const historyWithContext = [
              ...createAthleteReportContext(),
              ...messages.slice(0, -1),
            ];

            await geminiService.startChat(historyWithContext); // Initialize with context + all messages except the last user message

            const lastUserMessage = messages[messages.length - 1];
            const response = await geminiService.sendMessage(
              lastUserMessage.content,
            );

            const assistantMessage: Message = {
              id: (Date.now() + 1).toString(),
              role: "model",
              content: response,
              timestamp: new Date(),
            };

            setMessages((prev) => [...prev, assistantMessage]);
            await conversationService.saveMessage(
              currentUser!.uid,
              chat.id!,
              assistantMessage,
            );

            setIsLoading(false);
          }
        } else {
          console.log(
            "🔄 EXISTING CONVERSATION FLOW - Initializing Gemini chat with history:",
            messages.length,
            "messages",
          );

          // Include athlete report context at the beginning
          const messagesWithContext = [
            ...createAthleteReportContext(),
            ...messages,
          ];

          await geminiService.startChat(messagesWithContext);

          // For existing conversations, just initialize the chat with history
          // No auto-response needed since this is an existing conversation
        }

        setChatInitialized(true);
      } catch (err) {
        console.error("Error initializing Gemini chat:", err);
        setChatInitialized(true);
        setIsLoading(false);
        setErrorMessage(
          err instanceof Error
            ? err.message
            : "Failed to get response. Please try again.",
        );
        const lastUserContent = messages[messages.length - 1]?.content;
        if (lastUserContent) {
          setFailedUserMessage(lastUserContent);
        }
      }
    };

    initializeGeminiChat();
  }, [
    dataLoaded,
    dayPostsLoading,
    chat,
    chatInitialized,
    isNewConversation,
    currentUser,
  ]);

  const handleSendMessage = async () => {
    const content = input.trim();

    if (!content || isLoading || !currentUser || !chat?.id) {
      return;
    }
    const hasStartedChatToday = await userService.getTodayConversation(
      currentUser.uid,
    );
    if (!isPremium && hasStartedChatToday) {
      setWarningTitle(FEATURE_GATES.aiChatLimit.title);
      setWarningMessage(FEATURE_GATES.aiChatLimit.description);
      setShowPremiumModal(true);
      return;
    }
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: content,
      timestamp: new Date(),
    };

    // Store the previous messages before adding the new one
    const previousMessages = [...messages];

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setErrorMessage(null);
    setFailedUserMessage(null);

    try {
      // Save user message to database
      await conversationService.saveMessage(
        currentUser.uid,
        chat.id,
        userMessage,
      );

      if (!chatInitialized) {
        // Initialize chat with messages BEFORE the current user message
        // Use previousMessages instead of messages to avoid including the just-added message

        // Include athlete report context at the beginning
        const messagesWithContext = [
          ...createAthleteReportContext(),
          ...previousMessages,
        ];

        await geminiService.startChat(messagesWithContext);
        setChatInitialized(true);
      }

      const response = await geminiService.sendMessage(content);

      // Check if response is empty or invalid
      if (!response || response.trim() === "") {
        throw new Error("Received empty response from AI");
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "model",
        content: response,
        timestamp: new Date(),
      };
      // Save assistant message to database
      await conversationService.saveMessage(
        currentUser.uid,
        chat.id,
        assistantMessage,
      );

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error("Error sending message:", err);

      // Set error state
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Failed to get response. Please try again.",
      );
      setFailedUserMessage(content);

      // Remove the user message from the UI since it failed
      setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Only send on Enter without Shift (Shift+Enter adds new line)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRetry = () => {
    if (!failedUserMessage) return;

    const lastMessage = messages[messages.length - 1];
    const alreadyOnScreen =
      lastMessage?.role === "user" &&
      lastMessage.content === failedUserMessage;

    setErrorMessage(null);
    setFailedUserMessage(null);

    if (alreadyOnScreen) {
      initialMessageSent.current = false;
      setChatInitialized(false);
      return;
    }

    setInput(failedUserMessage);
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  return (
    <div className="w-full pt-16">
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20">
          <div className="relative">
            <button
              onClick={() => {
                setWarningTitle("");
                setWarningMessage("");
                setShowPremiumModal(false);
              }}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon
              featureKey="aiChatLimit"
              title={warningTitle}
              description={warningMessage}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col max-w-3xl mx-auto relative px-8">
        {/* Floating Back button */}
        <button
          onClick={() => navigate("/chats")}
          className="fixed left-4 top-[74.5px] z-50 p-2 bg-base-300 rounded-lg shadow-md hover:shadow-lg transition-shadow"
          title="Back to Chats"
        >
          <svg
            className="w-6 h-6 text-base-content"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Chat messages area */}
        <div className="space-y-4 pt-8 pb-[240px]">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`p-4 rounded-tl-2xl rounded-bl-2xl rounded-br-2xl rounded-tr-[4px] max-w-2xl w-fit ${
                  message.role === "user"
                    ? "bg-neutral font-geist text-sm"
                    : "text-base-content/70 font-grotesk"
                }`}
              >
                {message.role === "user" ? (
                  <p className="text-white font-light">{message.content}</p>
                ) : (
                  <div className="flex gap-4">
                    <img
                      src={icon}
                      alt="Send"
                      className="h-6 w-6 mt-1 bg-neutral p-1 rounded-md"
                    />

                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p className="mb-4 last:mb-0">{children}</p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold text-base-content">
                              {children}
                            </strong>
                          ),
                          em: ({ children }) => (
                            <em className="italic">{children}</em>
                          ),
                          ul: ({ children }) => (
                            <ul className="list-disc ml-4 mb-4 space-y-1">
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="list-decimal ml-4 mb-4 space-y-1">
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="pl-2">{children}</li>
                          ),
                          h1: ({ children }) => (
                            <h1 className="text-lg font-bold mb-4">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-base font-bold mb-4">
                              {children}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-sm font-bold mb-1">
                              {children}
                            </h3>
                          ),
                          code: ({ children }) => (
                            <code className="bg-base-300 px-1 py-0.5 rounded text-sm font-mono">
                              {children}
                            </code>
                          ),
                          pre: ({ children }) => (
                            <pre className="bg-base-300 p-2 rounded text-sm font-mono overflow-x-auto mb-2">
                              {children}
                            </pre>
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-base-200 p-4 rounded-tl-xl rounded-bl-xl rounded-br-xl max-w-[90%] flex gap-4">
                <img src={icon} alt="Send" className="h-4 w-4 mt-1" />
                <p className="text-base-content/80 font-geist">
                  Pondering & reasoning ...
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {errorMessage && !isLoading && (
            <div className="flex justify-center">
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 max-w-xl w-full">
                <div className="flex items-start space-x-3">
                  <svg
                    className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1 ">
                    <p className="text-red-700 dark:text-red-300 font-medium">
                      Failed to get response
                    </p>
                    <p className="text-red-600 text-sm mt-1">{errorMessage}</p>
                    <button
                      onClick={handleRetry}
                      className="mt-3 px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-700 dark:text-red-400 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Retry Message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Chat input */}
        <div className="pb-12 fixed bottom-0 left-0 right-0 px-8 sm:px-8">
          <div className="max-w-3xl mx-auto bg-base-100 border border-base-300 rounded-2xl p-3 sm:p-4 font-geist transition-all w-full flex items-end gap-2">
            <textarea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Write a message..."
              className="flex-1 resize-none border-none bg-transparent focus:outline-none placeholder-base-content/60 text-sm sm:text-base font-light p-4"
              disabled={isLoading}
              autoFocus
            />
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || isLoading}
              className="p-2 mb-1 bg-neutral/80 rounded-lg text-base-content/50 hover:text-base-content/70 disabled:opacity-0 transition-colors flex-shrink-0"
            >
              <img src={icon} alt="PEAK logo" className="h-5 w-auto" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPrompt;
